import { useEffect, useState } from "react";
import { Flag, Loader2, Check, X } from "lucide-react";
import { Drawer, DrawerContent } from "@/components/ui/drawer";
import { useLanguage } from "@/i18n/LanguageProvider";
import { useReportedContent } from "@/contexts/ReportedContentContext";
import {
  DETAILS_MAX_CHARS,
  REPORT_REASONS,
  type ReportReason,
  type ReportSurface,
  type SnapshotKind,
} from "@/lib/report/surfaces";

/**
 * ReportContentSheet — o fluxo de denuncia, INTEIRO DENTRO DO APP.
 *
 * A politica do Google e literal: "without needing to exit the app". Isso
 * elimina de saida as tres saidas baratas que costumam aparecer no lugar de um
 * formulario de verdade: `mailto:` (abre o cliente de e-mail), link para o
 * navegador e formulario web em WebView externa. Aqui o toque abre um Drawer,
 * o motivo e um botao, o envio e um INSERT, e a confirmacao acontece na mesma
 * folha. Nada sai do processo.
 *
 * Tres passos: motivo -> (opcional) texto livre -> confirmacao explicita. A
 * confirmacao nao e um toast que passa: e um estado da folha, com o que
 * aconteceu com o conteudo escrito em uma frase, porque o usuario acabou de
 * ver o item sumir da tela e precisa saber que foi ele quem causou isso.
 */

export interface ReportContentTarget {
  surface: ReportSurface;
  contentId: string;
  snapshot: string;
  snapshotKind?: SnapshotKind;
  context?: Record<string, unknown>;
  /**
   * Resolve o snapshot no MOMENTO DO ENVIO, para superficies cujo texto nao
   * esta em memoria. O caso concreto e a Biblioteca: a lista nao carrega
   * `content_text` (seriam KBs por linha x 100), entao sem isto o relato de um
   * entregavel de texto guardaria so o titulo — inutil para moderar, que e o
   * unico uso que a politica exige da denuncia.
   *
   * Falhou? cai no `snapshot` sincrono. Nunca bloqueia o envio: um relato com
   * snapshot pobre vale mais que um relato perdido.
   */
  resolveSnapshot?: () => Promise<string>;
}

interface ReportContentSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  target: ReportContentTarget | null;
  /**
   * Chamado DEPOIS que a linha esta no banco. Existe para a superficie cujo
   * corte nao e "esconder" e sim "descartar": o modal de confirmacao da foto de
   * refeicao nunca chegou a gravar nada, entao a denuncia fecha o modal e a
   * analise morre ali. Nao e chamado quando o INSERT falha.
   */
  onReported?: () => void;
}

const ReportContentSheet = ({ open, onOpenChange, target, onReported }: ReportContentSheetProps) => {
  const { t, lang } = useLanguage();
  const { submitReport } = useReportedContent();

  const [reason, setReason] = useState<ReportReason | null>(null);
  const [details, setDetails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  // Reset ao abrir: a folha e reusada por varias superficies, e herdar o motivo
  // da denuncia anterior faria o usuario enviar a categoria errada sem notar.
  useEffect(() => {
    if (open) {
      setReason(null);
      setDetails("");
      setSubmitting(false);
      setDone(false);
      setFailed(false);
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!target || !reason || submitting) return;
    setSubmitting(true);
    setFailed(false);
    try {
      let snapshot = target.snapshot;
      if (target.resolveSnapshot) {
        try {
          const resolved = await target.resolveSnapshot();
          if (resolved && resolved.trim().length > 0) snapshot = resolved;
        } catch (err) {
          // Degrada para o snapshot sincrono — nunca aborta a denuncia.
          console.warn("[ReportContentSheet] snapshot nao resolvido", err);
        }
      }
      await submitReport({
        surface: target.surface,
        contentId: target.contentId,
        reason,
        details,
        snapshot,
        snapshotKind: target.snapshotKind,
        context: target.context,
        language: lang,
      });
      setDone(true);
      onReported?.();
    } catch (err) {
      // O conteudo NAO foi escondido (o contexto reverteu). Dizer "recebemos"
      // aqui seria a falha silenciosa classica: usuario tranquilo, linha
      // nenhuma no banco, e a moderacao sem o caso.
      console.error("[ReportContentSheet] envio falhou", err);
      setFailed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="border-t"
        style={{ background: "hsl(222 20% 9%)", borderColor: "hsl(222 18% 18%)" }}
      >
        <div className="px-5 pb-8 pt-2 max-w-lg mx-auto w-full">
          {done ? (
            <div className="py-6 text-center">
              <div
                className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
                style={{ background: "hsl(142 60% 45% / 0.14)" }}
              >
                <Check size={22} style={{ color: "hsl(142 60% 55%)" }} />
              </div>
              <h2 className="text-[17px] font-display font-medium text-foreground mb-2">
                {t("report.done.title")}
              </h2>
              <p className="text-[13px] font-body text-foreground/60 leading-relaxed mb-6">
                {t("report.done.body")}
              </p>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="w-full rounded-2xl py-3 text-[14px] font-display font-medium text-foreground"
                style={{ background: "hsl(222 18% 15%)", border: "1px solid hsl(222 18% 22%)" }}
              >
                {t("report.done.close")}
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 mb-1 pt-2">
                <div className="flex items-center gap-2">
                  <Flag size={16} style={{ color: "hsl(0 70% 62%)" }} />
                  <h2 className="text-[17px] font-display font-medium text-foreground">
                    {t("report.sheet.title")}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => onOpenChange(false)}
                  aria-label={t("report.sheet.cancel")}
                  className="p-1 -mr-1 text-foreground/45"
                >
                  <X size={18} />
                </button>
              </div>
              <p className="text-[13px] font-body text-foreground/55 leading-relaxed mb-5">
                {t("report.sheet.subtitle")}
              </p>

              <p className="text-[12px] font-display uppercase tracking-wide text-foreground/40 mb-2">
                {t("report.sheet.reasonLabel")}
              </p>
              <div className="flex flex-col gap-1.5 mb-5">
                {REPORT_REASONS.map((r) => {
                  const selected = reason === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setReason(r)}
                      aria-pressed={selected}
                      className="text-left rounded-xl px-3.5 py-2.5 text-[14px] font-body transition-colors"
                      style={{
                        background: selected ? "hsl(187 72% 50% / 0.12)" : "hsl(222 18% 12%)",
                        border: selected
                          ? "1px solid hsl(187 72% 50% / 0.55)"
                          : "1px solid hsl(222 18% 18%)",
                        color: selected ? "hsl(187 72% 62%)" : "hsl(210 20% 82%)",
                      }}
                    >
                      {t("report.reason." + r)}
                    </button>
                  );
                })}
              </div>

              <label
                htmlFor="report-details"
                className="block text-[12px] font-display uppercase tracking-wide text-foreground/40 mb-2"
              >
                {t("report.sheet.detailsLabel")}
              </label>
              <textarea
                id="report-details"
                value={details}
                onChange={(e) => setDetails(e.target.value.slice(0, DETAILS_MAX_CHARS))}
                placeholder={t("report.sheet.detailsPlaceholder")}
                rows={3}
                className="w-full rounded-xl px-3.5 py-2.5 text-[14px] font-body text-foreground resize-none outline-none"
                style={{ background: "hsl(222 18% 12%)", border: "1px solid hsl(222 18% 18%)" }}
              />

              {failed && (
                <p className="text-[13px] font-body mt-3" style={{ color: "hsl(0 70% 65%)" }}>
                  {t("report.sheet.error")}
                </p>
              )}

              <button
                type="button"
                onClick={handleSubmit}
                disabled={!reason || submitting}
                className="w-full rounded-2xl py-3 mt-5 text-[14px] font-display font-medium flex items-center justify-center gap-2 disabled:opacity-40"
                style={{ background: "hsl(187 72% 50%)", color: "hsl(222 30% 8%)" }}
              >
                {submitting && <Loader2 size={15} className="animate-spin" />}
                {t(failed ? "report.sheet.retry" : "report.sheet.submit")}
              </button>
              <p className="text-[11px] font-body text-foreground/35 text-center mt-3 leading-relaxed">
                {t("report.sheet.footnote")}
              </p>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ReportContentSheet;
